import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

function AdminControlPanel() {
  const [users, setUsers] = useState([]);
  const [msg, setMsg] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [formData, setFormData] = useState({});

  const getForm = (userId) => formData[userId] || {
    currency: 'ETH', amount: '', subject: '', body: '', wMsg: '',
    overrideCurrency: 'ETH', overrideAmount: '',
  };
  const setForm = (userId, updates) => setFormData(prev => ({ ...prev, [userId]: { ...getForm(userId), ...updates } }));

  // ✅ cache‑buster added to bypass Cloudflare cached preflight
  const fetchUsers = async () => {
    try {
      const res = await api.get(`/api/admin/users?_=${Date.now()}`);
      setUsers(res.data);
    } catch (err) {
      setMsg('❌ Could not load users');
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const fundUser = async (user) => {
    const { amount, currency } = getForm(user.id);
    if (!amount || amount <= 0) return setMsg('❌ Enter a valid amount');
    try {
      const res = await api.post('/api/admin/fund', { email: user.email, amount: Number(amount), currency });
      setMsg(`✅ Funded ${amount} ${currency}. New balance: ${res.data.newBalance}`);
      fetchUsers();
      setForm(user.id, { amount: '' });
    } catch (err) {
      setMsg(`❌ ${err.response?.data?.error || 'Fund failed'}`);
    }
  };

  const sendEmail = async (user) => {
    const { subject, body } = getForm(user.id);
    if (!subject || !body) return setMsg('❌ Subject and message are required');
    try {
      await api.post('/api/admin/send-email', { email: user.email, subject, message: body });
      setMsg('✅ Email sent');
      setForm(user.id, { subject: '', body: '' });
    } catch (err) {
      setMsg(`❌ ${err.response?.data?.error || 'Email failed'}`);
    }
  };

  const saveWithdrawalMsg = async (user) => {
    const { wMsg } = getForm(user.id);
    try {
      await api.patch(`/api/admin/users/${user.id}/withdrawal-message`, { message: wMsg });
      setMsg('✅ Withdrawal message saved');
    } catch (err) {
      setMsg(`❌ ${err.response?.data?.error || 'Save failed'}`);
    }
  };

  const handleManualBalance = async (user) => {
    const currency = getForm(user.id).overrideCurrency || 'ETH';
    const amount = getForm(user.id).overrideAmount;
    if (!amount || isNaN(amount)) return setMsg('❌ Enter a valid balance');
    try {
      await api.patch(`/api/admin/users/${user.id}/balance`, {
        balance: parseFloat(amount),
        currency,
      });
      setMsg(`✅ ${currency} balance set to ${amount}`);
      fetchUsers();
      setForm(user.id, { overrideAmount: '', overrideCurrency: 'ETH' });
    } catch (err) {
      setMsg(`❌ ${err.response?.data?.error || 'Balance update failed'}`);
    }
  };

  return (
    <div>
      <h2>⚙️ Admin Control Panel</h2>

      {msg && (
        <div style={{
          background: msg.startsWith('✅') ? '#d1fae5' : '#fee2e2',
          color: '#065f46', padding: '1rem', borderRadius: '0.5rem',
          marginBottom: '1.5rem', fontWeight: 600,
          border: `2px solid ${msg.startsWith('✅') ? '#10b981' : '#ef4444'}`
        }}>
          {msg}
          <button type="button" onClick={() => setMsg('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>✖</button>
        </div>
      )}

      {/* Quick admin tools */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3>Quick Tools</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <Link to="/admin/kyc" className="copy-btn" style={{ background: '#3b82f6', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem' }}>📄 Manage KYC</Link>
          <Link to="/admin/tickets" className="copy-btn" style={{ background: '#3b82f6', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem' }}>🎫 Support Tickets</Link>
          <Link to="/admin/logs" className="copy-btn" style={{ background: '#3b82f6', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem' }}>📋 Audit Logs</Link>
          <Link to="/admin/withdrawals" className="copy-btn" style={{ background: '#3b82f6', color: 'white', padding: '0.5rem 1rem', borderRadius: '0.5rem' }}>💸 Withdrawals</Link>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <h3>Users</h3>
        <div className="table-box" style={{ marginTop: '1rem' }}>
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Balance</th><th>KYC</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(user => {
                const isOpen = expandedId === user.id;
                return (
                  <React.Fragment key={user.id}>
                    <tr>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td>{user.balance}</td>
                      <td style={{ color: user.kycStatus === 'verified' ? 'green' : 'orange' }}>{user.kycStatus}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => setExpandedId(isOpen ? null : user.id)}
                          style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.3rem 0.7rem', borderRadius: '0.25rem', cursor: 'pointer' }}
                        >
                          {isOpen ? 'Close' : 'Manage'}
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6}>
                          <div style={{ background: '#f1f5f9', padding: '1.5rem', borderRadius: '0.75rem', margin: '1rem 0' }}>
                            {/* Fund */}
                            <div style={{ marginBottom: '1.5rem' }}>
                              <h4>💸 Fund Account</h4>
                              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <select
                                  value={getForm(user.id).currency}
                                  onChange={e => setForm(user.id, { currency: e.target.value })}
                                  style={{ padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc' }}
                                >
                                  <option value="ETH">ETH</option>
                                  <option value="BTC">BTC</option>
                                  <option value="USDT">USDT</option>
                                </select>
                                <input
                                  type="number"
                                  placeholder="Amount"
                                  value={getForm(user.id).amount}
                                  onChange={e => setForm(user.id, { amount: e.target.value })}
                                  style={{ padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc', flex: 1 }}
                                />
                                <button
                                  type="button"
                                  onClick={() => fundUser(user)}
                                  style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.25rem', cursor: 'pointer' }}
                                >
                                  Fund
                                </button>
                              </div>
                            </div>

                            {/* Email – fixed vertical alignment */}
                            <div style={{ marginBottom: '1.5rem' }}>
                              <h4>📧 Send Custom Email</h4>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <input
                                  type="text"
                                  placeholder="Subject"
                                  value={getForm(user.id).subject}
                                  onChange={e => setForm(user.id, { subject: e.target.value })}
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc' }}
                                />
                                <textarea
                                  placeholder="Message"
                                  value={getForm(user.id).body}
                                  onChange={e => setForm(user.id, { body: e.target.value })}
                                  rows="3"
                                  style={{ width: '100%', padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc', resize: 'vertical' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => sendEmail(user)}
                                  style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.25rem', cursor: 'pointer', alignSelf: 'flex-start' }}
                                >
                                  Send Email
                                </button>
                              </div>
                            </div>

                            {/* Withdrawal Message */}
                            <div style={{ marginBottom: '1.5rem' }}>
                              <h4>📝 Default Withdrawal Message</h4>
                              <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                Shown to the user when they request a withdrawal.
                              </p>
                              <textarea
                                placeholder="Enter default message (optional)"
                                value={getForm(user.id).wMsg}
                                onChange={e => setForm(user.id, { wMsg: e.target.value })}
                                rows="2"
                                style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc' }}
                              />
                              <button
                                type="button"
                                onClick={() => saveWithdrawalMsg(user)}
                                style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.25rem', cursor: 'pointer' }}
                              >
                                Save Message
                              </button>
                            </div>

                            {/* Per‑Currency Manual Balance Override */}
                            <div>
                              <h4>💰 Manual Balance Override</h4>
                              <p style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                Current balances: <strong>BTC:</strong> {user.balanceBTC ?? 0}, <strong>ETH:</strong> {user.balanceETH ?? 0}, <strong>USDT:</strong> {user.balanceUSDT ?? 0}
                              </p>
                              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
                                <select
                                  value={getForm(user.id).overrideCurrency}
                                  onChange={e => setForm(user.id, { overrideCurrency: e.target.value })}
                                  style={{ padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc' }}
                                >
                                  <option value="BTC">BTC</option>
                                  <option value="ETH">ETH</option>
                                  <option value="USDT">USDT</option>
                                </select>
                                <input
                                  type="number"
                                  placeholder="New balance"
                                  value={getForm(user.id).overrideAmount}
                                  onChange={e => setForm(user.id, { overrideAmount: e.target.value })}
                                  style={{ padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid #ccc', flex: 1 }}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleManualBalance(user)}
                                  style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '0.25rem', cursor: 'pointer' }}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminControlPanel;